import React, { useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { Users, Target, CheckCircle2, AlertTriangle, Briefcase, Star } from 'lucide-react';

interface TalentPoolStatisticsProps {
    candidates: any[];
    precomputedStats?: any;
}

const STATUS_COLORS: Record<string, string> = {
    'Lead': '#64748b',
    'Shortlist': '#f59e0b',
    'Op shortlist': '#f59e0b',
    'Actief': '#3b82f6',
    'In behandeling': '#3b82f6',
    'Beschikbaar (Open to work)': '#10b981',
    'Onder contract': '#6366f1',
    'Geplaatst': '#22c55e',
    'Gearchiveerd': '#94a3b8',
    'Blacklist': '#ef4444',
};

const BAR_COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe', '#f5f3ff', '#818cf8', '#4f46e5', '#4338ca'];

const TalentPoolStatistics: React.FC<TalentPoolStatisticsProps> = ({ candidates, precomputedStats }) => {

    // --- Real data calculations ---
    const total = precomputedStats ? precomputedStats.total : candidates.length;

    const statusMap = useMemo(() => {
        if (precomputedStats?.status_counts) return precomputedStats.status_counts;
        const map: Record<string, number> = {};
        candidates.forEach(c => {
            const s = c.status || 'Onbekend';
            map[s] = (map[s] || 0) + 1;
        });
        return map;
    }, [candidates, precomputedStats]);

    const statusData = useMemo(() =>
        Object.entries(statusMap)
            .sort(([, a], [, b]) => (b as number) - (a as number))
            .map(([name, value]) => ({ name, value, pct: total ? Math.round(((value as number) / total) * 100) : 0 }))
    , [statusMap, total]);

    const active = precomputedStats ? (total - ((precomputedStats.status_counts['Gearchiveerd'] || 0) + (precomputedStats.status_counts['Blacklist'] || 0) + (precomputedStats.status_counts['Geplaatst'] || 0))) : candidates.filter(c =>
        !['Gearchiveerd', 'Blacklist', 'Geplaatst'].includes(c.status)
    ).length;

    const withCV = useMemo(() => candidates.filter(c => {
        const d = c.ai_parsed_data || c.parsed_cv || {};
        return (d.timeline?.length > 0) || (d.education?.length > 0) || d.raw_cv_text;
    }).length, [candidates]);

    const withContact = useMemo(() =>
        candidates.filter(c => c.candidate?.email || c.candidate?.phone).length
    , [candidates]);

    const seniorityData = useMemo(() => {
        if (precomputedStats?.experience_counts) {
            return Object.entries(precomputedStats.experience_counts).map(([name, value]) => {
                let color = '#e2e8f0';
                if (name.includes('Junior')) color = '#a78bfa';
                if (name.includes('Medior')) color = '#6366f1';
                if (name.includes('Senior')) color = '#4338ca';
                return { name, value, color };
            }).filter(d => (d.value as number) > 0);
        }
        let junior = 0, medior = 0, senior = 0, unknown = 0;
        candidates.forEach(c => {
            const exp = c.candidate?.experience || c.ai_parsed_data?.years_experience || 0;
            if (!exp) unknown++;
            else if (exp <= 2) junior++;
            else if (exp <= 5) medior++;
            else senior++;
        });
        return [
            { name: 'Junior', value: junior, color: '#a78bfa' },
            { name: 'Medior', value: medior, color: '#6366f1' },
            { name: 'Senior', value: senior, color: '#4338ca' },
            { name: 'Onbekend', value: unknown, color: '#e2e8f0' },
        ].filter(d => d.value > 0);
    }, [candidates, precomputedStats]);

    const skillsData = useMemo(() => {
        if (precomputedStats?.skills_counts) {
            return Object.entries(precomputedStats.skills_counts).map(([name, count]) => ({ name, count })).sort((a, b) => (b.count as number) - (a.count as number));
        }
        const map: Record<string, number> = {};
        candidates.forEach(c => {
            const tags = c.ai_parsed_data?.tags || c.parsed_cv?.tags || c.candidate?.skill_set || [];
            const arr = Array.isArray(tags) ? tags : typeof tags === 'string' ? tags.split(',').map((s: string) => s.trim()) : [];
            arr.forEach((s: string) => {
                if (s) map[s] = (map[s] || 0) + 1;
            });
        });
        return Object.entries(map)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 10)
            .map(([name, count]) => ({ name, count }));
    }, [candidates, precomputedStats]);

    const roleData = useMemo(() => {
        const map: Record<string, number> = {};
        candidates.forEach(c => {
            const d = c.ai_parsed_data || c.parsed_cv || {};
            const role = (c.candidate?.role || d.currentRole || d.role || d.timeline?.[0]?.role || '').trim();
            if (role) map[role] = (map[role] || 0) + 1;
        });
        return Object.entries(map)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 8)
            .map(([name, value]) => ({ name, value }));
    }, [candidates]);

    const recentlyAdded = useMemo(() => {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return candidates.filter(c => new Date(c.created_at) > thirtyDaysAgo).length;
    }, [candidates]);

    const oldProfiles = useMemo(() => {
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        return candidates.filter(c => new Date(c.created_at) < sixMonthsAgo).length;
    }, [candidates]);

    if (total === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                <Users size={32} className="mb-2 opacity-30" />
                <p className="text-sm font-medium">Geen kandidaten beschikbaar voor statistieken.</p>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto custom-scrollbar pb-10 pr-1">
            {/* KPI Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <KpiCard label="Totaal" value={total} sub={`+${recentlyAdded} afgelopen 30d`} accent="slate" />
                <KpiCard label="Actief" value={active} sub={`${total ? Math.round((active/total)*100) : 0}% van totaal`} accent="indigo" />
                <KpiCard label="Met CV data" value={withCV} sub={`${total ? Math.round((withCV/total)*100) : 0}% compleet`} accent="emerald" />
                <KpiCard label="Met contactinfo" value={withContact} sub={`${total ? Math.round((withContact/total)*100) : 0}% bereikbaar`} accent="amber" />
            </div>

            {/* Main 3-col grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">

                {/* Status breakdown */}
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
                    <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <Star size={11} /> Status verdeling
                    </h3>
                    <div className="space-y-2">
                        {statusData.map(({ name, value, pct }) => (
                            <div key={name}>
                                <div className="flex justify-between items-center mb-0.5">
                                    <span className="text-[11px] font-medium text-slate-600 truncate max-w-[140px]">{name}</span>
                                    <span className="text-[11px] font-semibold text-slate-500 shrink-0 ml-2">{value} <span className="text-slate-300">({pct}%)</span></span>
                                </div>
                                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all"
                                        style={{ width: `${pct}%`, backgroundColor: STATUS_COLORS[name] || '#94a3b8' }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Seniority bar chart */}
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
                    <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <Users size={11} /> Senioriteit
                    </h3>
                    <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={seniorityData} barSize={32}>
                            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                            <YAxis hide />
                            <Tooltip
                                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 11 }}
                                cursor={{ fill: '#f8fafc' }}
                            />
                            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                {seniorityData.map((entry, i) => (
                                    <Cell key={i} fill={entry.color} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap gap-2 mt-1">
                        {seniorityData.map(d => (
                            <span key={d.name} className="flex items-center gap-1 text-[10px] text-slate-500">
                                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: d.color }} />
                                {d.name} {d.value}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Data quality */}
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
                    <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <CheckCircle2 size={11} /> Datakwaliteit
                    </h3>
                    <div className="space-y-3">
                        <QualityItem
                            label="Profiel compleetheid"
                            value={`${total ? Math.round((withCV / total) * 100) : 0}%`}
                            pct={total ? Math.round((withCV / total) * 100) : 0}
                            color="#10b981"
                        />
                        <QualityItem
                            label="Contactinfo aanwezig"
                            value={`${total ? Math.round((withContact / total) * 100) : 0}%`}
                            pct={total ? Math.round((withContact / total) * 100) : 0}
                            color="#6366f1"
                        />
                        <div className="pt-1 border-t border-slate-50 space-y-2 mt-2">
                            <div className="flex justify-between items-center">
                                <span className="text-[11px] text-slate-500">Toegevoegd afgelopen 30d</span>
                                <span className="text-[11px] font-semibold text-emerald-600">+{recentlyAdded}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-[11px] text-slate-500 flex items-center gap-1">
                                    {oldProfiles > 50 && <AlertTriangle size={10} className="text-amber-500" />}
                                    Profiel ouder dan 6 maanden
                                </span>
                                <span className={`text-[11px] font-semibold ${oldProfiles > 50 ? 'text-amber-500' : 'text-slate-400'}`}>{oldProfiles}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom 2-col: Top skills + Top functies */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Top skills */}
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
                    <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <Target size={11} /> Top skills
                    </h3>
                    {skillsData.length === 0 ? (
                        <p className="text-[11px] text-slate-300 italic">Geen skill data beschikbaar.</p>
                    ) : (
                        <div className="space-y-1.5">
                            {skillsData.map(({ name, count }, i) => (
                                <div key={name} className="flex items-center gap-2">
                                    <span className="text-[10px] font-mono text-slate-300 w-4 text-right shrink-0">{i + 1}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-center mb-0.5">
                                            <span className="text-[11px] font-medium text-slate-700 truncate">{name}</span>
                                            <span className="text-[10px] font-semibold text-slate-400 shrink-0 ml-2">{count}</span>
                                        </div>
                                        <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full"
                                                style={{ width: `${Math.round((count / (skillsData[0]?.count || 1)) * 100)}%`, backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Top functies */}
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
                    <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <Briefcase size={11} /> Top functies & rollen
                    </h3>
                    {roleData.length === 0 ? (
                        <p className="text-[11px] text-slate-300 italic">Geen functie data beschikbaar.</p>
                    ) : (
                        <div className="space-y-1.5">
                            {roleData.map(({ name, value }, i) => (
                                <div key={name} className="flex items-center gap-2">
                                    <span className="text-[10px] font-mono text-slate-300 w-4 text-right shrink-0">{i + 1}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-center mb-0.5">
                                            <span className="text-[11px] font-medium text-slate-700 truncate">{name}</span>
                                            <span className="text-[10px] font-semibold text-slate-400 shrink-0 ml-2">{value}</span>
                                        </div>
                                        <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full bg-violet-400"
                                                style={{ width: `${Math.round((value / (roleData[0]?.value || 1)) * 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Helper Components ---

const KpiCard = ({ label, value, sub, accent }: { label: string; value: number; sub: string; accent: string }) => {
    const colors: Record<string, string> = {
        slate: 'text-slate-800',
        indigo: 'text-indigo-600',
        emerald: 'text-emerald-600',
        amber: 'text-amber-600',
    };
    return (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm px-4 py-3">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{label}</p>
            <p className={`text-2xl font-bold ${colors[accent] || 'text-slate-800'}`}>{value.toLocaleString('nl-NL')}</p>
            <p className="text-[10px] text-slate-400 font-medium mt-0.5">{sub}</p>
        </div>
    );
};

const QualityItem = ({ label, value, pct, color }: { label: string; value: string; pct: number; color: string }) => (
    <div>
        <div className="flex justify-between items-center mb-0.5">
            <span className="text-[11px] font-medium text-slate-600">{label}</span>
            <span className="text-[11px] font-semibold" style={{ color }}>{value}</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
        </div>
    </div>
);

export default TalentPoolStatistics;
